import { Grid } from '@interstate/components/Grid';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import type { PaymentGridRow, TermMonthItem } from '@makemydeal/dr-platform-types';
import { LEASE } from '@makemydeal/dr-platform-types';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useSelector } from 'react-redux';
import IsTargetOutOfPocket from '../common/IsTargetOutOfPocket';
import { getKeyWithLoadingItem } from '../common/utils';
import AdjustedResiduals from './AdjustedResiduals';
import { useIsExplicitLenderProgramGridActive } from './hooks/useIsExplicitLenderProgramGridActive';
import { CashDownCell } from './CashDownCell';
import { FlexBuyPaymentGridItemsRows } from './FlexBuyPaymentGridItemsRows';
import { FlexBuyTermAndPercentRow } from './FlexBuyTermAndPercentRow';
import { PaymentGridItemsRows } from './PaymentGridItemsRows';
import PaymentGridNote from './PaymentGridNote';
import Residuals from './Residuals';
import SellRates from './SellRates';
import { StyledGridContainer, StyledPaymentGridContainer } from './styled.components';
import TermsMonthRow from './TermsMonth';

const GridTable = () => {
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const paymentGridItems: PaymentGridRow[] = useSelector(offerReduxSelectors.getPaymentGridItems);
    const errorState = useSelector(offerReduxSelectors.getPaymentGridErrorState);
    const paymentGridTermList: TermMonthItem[] = useSelector(offerReduxSelectors.getPaymentGridTermsList);
    const isTargetOutOfPocketLeaseEnabled = useSelector(featureToggleSelectors.isTargetOutOfPocketLeaseEnabled);
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const flexBuyPaymentGridData = useSelector(offerReduxSelectors.getFlexBuyPaymentGridData);
    const isOutOfPocketLeaseActive = !isDealCentralExperience && isTargetOutOfPocketLeaseEnabled && offerType === LEASE;
    // Hold the grid until every cell with a term carries a payment, so a part-built grid is never shown.
    const isPaymentGridResolving = useSelector(offerReduxSelectors.isPaymentGridResolving);
    const columnView = useSelector(offerReduxSelectors.getPaymentGridColumnView);
    // Lease scopes loading to the columns and rows actually in flight and so takes no grid-wide flag - one column
    // pricing used to disable every other column's term and sell-rate input, including a term-less one waiting to be
    // filled in. The leaf inputs read per-column state themselves.
    const isLoadingPaymentGrid = columnView.isMasked ? false : getKeyWithLoadingItem(paymentGridItems) || isPaymentGridResolving;
    const isLoadingFlexBuyPaymentGrid =
        isFlexBuyPayment && !!flexBuyPaymentGridData && getKeyWithLoadingItem(flexBuyPaymentGridData);
    const isAspPaymentGridRedesignEnabled = useSelector(featureToggleSelectors.isAspPaymentGridRedesignEnabled);
    // FlexBuy runs its own grid with per-percent columns and no residual, so it stays out.
    const showResiduals = isAspPaymentGridRedesignEnabled && offerType === LEASE && !isFlexBuyPayment;
    const isExplicitLenderProgramGridActive = useIsExplicitLenderProgramGridActive();
    const showAdjustedResiduals = showResiduals && isExplicitLenderProgramGridActive;

    return (
        <StyledGridContainer>
            <StyledPaymentGridContainer data-testid="payment-grid" container $isDealCentralExperience={isDealCentralExperience}>
                {isOutOfPocketLeaseActive && (
                    <Grid xs={12} sx={{ paddingBottom: '10px' }}>
                        <IsTargetOutOfPocket disabled={true} />
                    </Grid>
                )}

                <CashDownCell />
                {isFlexBuyPayment && !!flexBuyPaymentGridData ? (
                    <>
                        <FlexBuyTermAndPercentRow
                            flexBuyPaymentGridData={flexBuyPaymentGridData}
                            isLoadingGrid={isLoadingFlexBuyPaymentGrid}
                        />
                        <FlexBuyPaymentGridItemsRows
                            key={JSON.stringify({ flexBuyPaymentGridData })}
                            flexBuyPaymentGridData={flexBuyPaymentGridData}
                            isLoadingGrid={isLoadingFlexBuyPaymentGrid}
                        />
                    </>
                ) : (
                    <>
                        <TermsMonthRow paymentGridTermList={paymentGridTermList} isLoading={isLoadingPaymentGrid} />
                        <PaymentGridItemsRows
                            hasFailure={!!errorState.hasError}
                            paymentGridItems={paymentGridItems}
                            isLoading={isLoadingPaymentGrid}
                        />
                    </>
                )}
                <SellRates isLoading={isLoadingPaymentGrid || isLoadingFlexBuyPaymentGrid} />
                {showResiduals && <Residuals isLoading={isLoadingPaymentGrid} />}
                {showAdjustedResiduals && <AdjustedResiduals isLoading={isLoadingPaymentGrid} />}
                <PaymentGridNote />
            </StyledPaymentGridContainer>
        </StyledGridContainer>
    );
};

export default GridTable;
